// src/pages/ProfilePage.js
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../AuthContext";
import API_BASE from "../apiConfig";
import "./ProfilePage.css";
import BackToDashboard from "../components/BackToDashboard";

function ProfilePage() {
  const navigate = useNavigate();
  const { currentCompany, setCurrentCompany, user: ctxUser } = useContext(AuthContext);

  const [logoUrl, setLogoUrl] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [isUploading, setIsUploading] = useState(false);

  // ---- helpers för id ----
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  // samma companyId-logik som Settings/NewCase
  const effectiveCompanyId = useMemo(() => {
    const cFromUser = toNum(ctxUser?.company_id);
    if (cFromUser) return cFromUser;

    const cFromCtx = toNum(currentCompany?.id);
    if (cFromCtx) return cFromCtx;

    try {
      const raw = localStorage.getItem("currentCompany");
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return toNum(obj?.id) || null;
    } catch {
      return null;
    }
  }, [ctxUser?.company_id, currentCompany?.id]);

  const nsCompany = effectiveCompanyId ?? currentCompany?.id ?? "anonCompany";
  const logoKey = useMemo(() => `companyLogo:${nsCompany}`, [nsCompany]);

  // Absolut URL för lagrad path (dev/prod)
  function getLogoUrl(profilePic) {
    if (!profilePic) return "";
    if (profilePic.startsWith("http") || profilePic.startsWith("//"))
      return profilePic;
    const assetBase =
      API_BASE === "/api" ? "" : API_BASE.replace(/\/api\/?$/, "");
    return assetBase + profilePic;
  }

  // Hämta om företagsdata efter uppladdning
  async function reloadCompanyData() {
    const res = await fetch(`${API_BASE}/companies`);
    const companies = await res.json();
    const updated = companies.find(
      (c) => String(c.id) === String(currentCompany.id)
    );
    if (updated) {
      localStorage.setItem("currentCompany", JSON.stringify(updated));
      setCurrentCompany(updated);
    }
    return updated;
  }

  // Initial load av logga
  useEffect(() => {
    if (!currentCompany) {
      navigate("/login");
      return;
    }

    const fromCompany = getLogoUrl(currentCompany.profilePic);
    const fromLs = localStorage.getItem(logoKey) || "";

    const final = fromCompany || fromLs || "";
    setLogoUrl(final);

    // synca in i LS så NewCasePage/SettingsPage hittar samma key
    if (final) {
      try {
        localStorage.setItem(logoKey, final);
      } catch {
        // ignore
      }
    }
  }, [currentCompany, navigate, logoKey]);

  const handleLogout = () => {
    localStorage.removeItem("currentCompany");
    // vi behåller companyLogo:<id> i LS – bra om samma företag loggar in igen
    setCurrentCompany(null);
    navigate("/login");
  };

  // Uppladdning + enkel validering + preview
  async function handleLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) {
      setUploadStatus("Ingen fil vald.");
      return;
    }

    // Validering
    if (!file.type.startsWith("image/")) {
      setUploadStatus("Endast bildfiler tillåtna.");
      return;
    }
    const MAX_MB = 3;
    if (file.size > MAX_MB * 1024 * 1024) {
      setUploadStatus(`Filen är för stor (max ${MAX_MB} MB).`);
      return;
    }

    // Direkt preview (endast i state, INTE i localStorage – blob: funkar inte efter reload)
    const localPreview = URL.createObjectURL(file);
    setLogoUrl(localPreview);

    setIsUploading(true);
    setUploadStatus("Laddar upp…");

    try {
      // Din befintliga endpoint: /upload-profile-picture (fält: profilePicture + companyId)
      const form1 = new FormData();
      form1.append("profilePicture", file);
      form1.append("companyId", String(currentCompany.id));

      let res = await fetch(`${API_BASE}/upload-profile-picture`, {
        method: "POST",
        body: form1,
      });
      let raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        throw new Error(raw);
      }

      // Fallback om första endpointen inte svarar som väntat
      if (!res.ok || data?.success === false) {
        const form2 = new FormData();
        form2.append("file", file);

        res = await fetch(`${API_BASE}/profile/logo`, {
          method: "POST",
          body: form2,
        });
        raw = await res.text();
        try {
          data = JSON.parse(raw);
        } catch {
          throw new Error(raw);
        }
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || "Uppladdning misslyckades");
        }
      }

      setUploadStatus("Uppladdad! Uppdaterar profil…");

      const updated = await reloadCompanyData();
      const finalUrl = getLogoUrl(updated?.profilePic);

      setLogoUrl(finalUrl);
      // 🔹 Spara permanenta URL:en per företag så NewCasePage kan hämta den
      if (finalUrl) {
        try {
          localStorage.setItem(logoKey, finalUrl);
        } catch {
          // ignore
        }
      }

      setUploadStatus("Profilbild uppdaterad!");
      setFileInputKey(Date.now()); // reset input
    } catch (err) {
      setUploadStatus("Fel vid uppladdning: " + String(err.message || err));
      // Rulla tillbaka preview till gamla bilden om något gick fel
      const fallback = getLogoUrl(currentCompany?.profilePic);
      setLogoUrl(fallback || "");
      if (fallback) {
        try {
          localStorage.setItem(logoKey, fallback);
        } catch {
          // ignore
        }
      }
    } finally {
      setIsUploading(false);
    }
  }

  function getRoleLabel(role) {
    const r = String(role || "").trim().toLowerCase();
    if (r === "admin") return "Admin";
    return "Användare";
  }

  if (!currentCompany) return null;

  return (
    <div className="profile-page">
      {/* Tillbaka-knapp centrerad */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 16,
        }}
      >
        <BackToDashboard mode="back" />
      </div>

      {/* Själva kortet */}
      <div className="profile-card">
        {/* Header med företagsnamn + roll + actions */}
        <div className="profile-header">
          <div>
            <h2 className="profile-title">
              {currentCompany.companyName || "-"}
            </h2>
            <span className="role-tag">
              {getRoleLabel(currentCompany.role)}
            </span>
          </div>

          <div className="profile-actions">
            <button
              className="btn"
              type="button"
              onClick={() => navigate("/settings")}
            >
              Grunduppgifter
            </button>
            <button
              className="btn btn-danger"
              type="button"
              onClick={handleLogout}
            >
              Logga ut
            </button>
          </div>
        </div>

        {/* Logga-badge */}
        <div className="profile-logo-wrap">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Företagslogga"
              className="profile-logo"
            />
          ) : (
            <div className="profile-logo placeholder">Företagslogga</div>
          )}
        </div>

        {/* Infofält */}
        <div className="profile-info-grid">
          <div className="profile-field">
            <label>Företagsnamn</label>
            <span>{currentCompany.companyName || "-"}</span>
          </div>
          <div className="profile-field">
            <label>Roll</label>
            <span>{getRoleLabel(currentCompany.role)}</span>
          </div>
          <div className="profile-field">
            <label>E-post</label>
            <span>{currentCompany.email || "-"}</span>
          </div>
        </div>

        {/* Byt logga */}
        <div className="profile-upload">
          <label htmlFor="logoUpload" className="profile-upload-label">
            Byt logga
          </label>
          <input
            type="file"
            id="logoUpload"
            accept="image/*"
            onChange={handleLogoChange}
            key={fileInputKey}
            disabled={isUploading}
          />
          {uploadStatus && (
            <div className="upload-status">{uploadStatus}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;